import { boundNumberToRange } from "@/pages/Tagging/utils/boundNumberToRange";

import { OUTS_PER_INNING } from "../constants/game";
import { endsAtBat, OUT_PLAY_RESULTS } from "../constants/tagOptions";
import { BaserunnerFlags, PitchTags } from "./pitchTagging";

export type AutoResults = {
  baseRunners: BaserunnerFlags;
  runsScored: number;
  outsRecorded: number;
};

/** Which Results From This Play fields the scorekeeper has changed by hand. */
export type ResultsEdited = {
  runsScored: boolean;
  outsRecorded: boolean;
  baseRunners: boolean;
};

export const NOT_EDITED: ResultsEdited = {
  runsScored: false,
  outsRecorded: false,
  baseRunners: false,
};

export const ALL_EDITED: ResultsEdited = {
  runsScored: true,
  outsRecorded: true,
  baseRunners: true,
};

/** Situation going into the pitch. */
export type SituationBefore = {
  baseRunners: BaserunnerFlags;
  outs: number;
};

const EMPTY_BASES: BaserunnerFlags = {
  first: false,
  second: false,
  third: false,
};

const countRunners = (bases: BaserunnerFlags) =>
  [bases.first, bases.second, bases.third].filter(Boolean).length;

const isOutResult = (playResult: string | null) =>
  !!playResult && OUT_PLAY_RESULTS.includes(playResult);

export const getMaxOuts = (before: SituationBefore) =>
  Math.max(0, OUTS_PER_INNING - before.outs);

export const getMaxRuns = (before: SituationBefore) =>
  countRunners(before.baseRunners) + 1;

/** Batter to first; only forced runners move up. */
const forceAdvance = (bases: BaserunnerFlags): AutoResults => ({
  baseRunners: {
    first: true,
    second: bases.second || bases.first,
    third: bases.third || (bases.second && bases.first),
  },
  runsScored: bases.first && bases.second && bases.third ? 1 : 0,
  outsRecorded: 0,
});

/** Every runner moves up one base; the runner from third scores. */
const advanceOne = (
  bases: BaserunnerFlags,
  batterOnFirst: boolean,
  outsRecorded: number,
): AutoResults => ({
  baseRunners: {
    first: batterOnFirst,
    second: bases.first,
    third: bases.second,
  },
  runsScored: bases.third ? 1 : 0,
  outsRecorded,
});

/** Pitch and play result of a tag, which together decide its results. */
export type PlayOutcome = Pick<PitchTags, "pitchResult" | "playResult">;

/** Runner advancement for a play result, before the third-out check. */
const getPlayAdvancement = (
  pitchResult: string | null,
  playResult: string | null,
  bases: BaserunnerFlags,
): AutoResults => {
  const runners = countRunners(bases);

  // A hit by pitch has no play result: the pitch result awards first
  if (pitchResult === "HitByPitch") return forceAdvance(bases);

  switch (playResult) {
    case "Walk":
      return forceAdvance(bases);
    case "Error":
    case "Single":
      // Catcher's interference awards first; reached on error plays like a single
      return pitchResult === "CatchersInt"
        ? forceAdvance(bases)
        : advanceOne(bases, true, 0);
    case "Double":
      return {
        baseRunners: { ...EMPTY_BASES, second: true },
        runsScored: runners,
        outsRecorded: 0,
      };
    case "Triple":
      return {
        baseRunners: { ...EMPTY_BASES, third: true },
        runsScored: runners,
        outsRecorded: 0,
      };
    case "HomeRun":
      return {
        baseRunners: EMPTY_BASES,
        runsScored: runners + 1,
        outsRecorded: 0,
      };
    case "Sacrifice":
      return advanceOne(bases, false, 1);
    case "Out":
    case "StrikeoutSwinging":
    case "StrikeoutLooking":
    case "FieldersChoice":
      return { baseRunners: bases, runsScored: 0, outsRecorded: 1 };
    default:
      // At bat continues
      return { baseRunners: bases, runsScored: 0, outsRecorded: 0 };
  }
};

/**
 * Pre-filled Results From This Play for a play result. Hits and sacrifices
 * use standard advancement; the scorekeeper corrects anything unusual.
 * Fielder's choice leaves the runners as they were for the scorekeeper to set.
 */
export const getAutoResults = (
  pitchResult: string | null,
  playResult: string | null,
  before: SituationBefore,
): AutoResults => {
  const results = getPlayAdvancement(
    pitchResult,
    playResult,
    before.baseRunners,
  );
  const maxOuts = getMaxOuts(before);

  // The third out ends the inning: no runs score and the bases clear
  if (results.outsRecorded >= maxOuts) {
    return { baseRunners: EMPTY_BASES, runsScored: 0, outsRecorded: maxOuts };
  }
  return results;
};

/**
 * Re-derives the results the scorekeeper hasn't edited for a new situation
 * before the pitch (e.g. the live game state was edited mid-tag). Hand edits
 * stay, and the untouched steppers rebalance around them.
 */
export const withSituationResults = (
  tags: PitchTags,
  edited: ResultsEdited,
  before: SituationBefore,
): PitchTags => {
  const auto = getAutoResults(tags.pitchResult, tags.playResult, before);
  const next = {
    ...tags,
    baseRunners: edited.baseRunners ? tags.baseRunners : auto.baseRunners,
    runsScored: edited.runsScored ? tags.runsScored : auto.runsScored,
    outsRecorded: edited.outsRecorded ? tags.outsRecorded : auto.outsRecorded,
  };
  const isAnyEdited =
    edited.baseRunners || edited.runsScored || edited.outsRecorded;
  return isAnyEdited
    ? { ...next, ...rebalanceResults(next, edited, next, before) }
    : next;
};

/**
 * Whether a tag change calls for new auto results: a new play result, or a
 * new pitch result where `getAutoResults` reads it (a hit by pitch, and
 * catcher's interference and reached on error sharing the Error play result).
 */
export const shouldRederiveResults = (prev: PitchTags, next: PitchTags) =>
  prev.playResult !== next.playResult ||
  (prev.pitchResult !== next.pitchResult &&
    (next.playResult === "Error" ||
      prev.pitchResult === "HitByPitch" ||
      next.pitchResult === "HitByPitch"));

/**
 * Runners who left the bases on the play (scored or put out), counting the
 * batter when the at bat ended:
 * runners before + batter = runners after + runs + outs
 */
export const getClearedRunners = (
  { pitchResult, playResult }: PlayOutcome,
  before: SituationBefore,
  basesAfter: BaserunnerFlags,
) =>
  Math.max(
    0,
    countRunners(before.baseRunners) +
      (endsAtBat(pitchResult, playResult) ? 1 : 0) -
      countRunners(basesAfter),
  );

/**
 * A play on the bases while the at bat goes on (a steal, caught stealing, a
 * pickoff, a wild pitch): the runner who left third scored, and any other
 * runner who left the bases was put out. A third out wipes the run.
 */
const getRunnerPlayResults = (
  results: AutoResults,
  before: SituationBefore,
  cleared: number,
): AutoResults => {
  const leftThird = before.baseRunners.third && !results.baseRunners.third;
  const runs = Math.min(cleared, leftThird ? 1 : 0);
  const maxOuts = getMaxOuts(before);
  const outs = Math.min(cleared - runs, maxOuts);
  return {
    ...results,
    runsScored: outs >= maxOuts ? 0 : runs,
    outsRecorded: outs,
  };
};

/**
 * Keeps runs + outs equal to the runners cleared by the play. Fields the
 * scorekeeper changed by hand are left alone; with neither stepper edited,
 * outs absorb the difference on out plays and runs on everything else, except
 * a play on the bases mid at bat (getRunnerPlayResults).
 */
export const rebalanceResults = (
  results: AutoResults,
  edited: ResultsEdited,
  outcome: PlayOutcome,
  before: SituationBefore,
): AutoResults => {
  if (edited.runsScored && edited.outsRecorded) return results;

  const cleared = getClearedRunners(outcome, before, results.baseRunners);
  if (
    !edited.runsScored &&
    !edited.outsRecorded &&
    !endsAtBat(outcome.pitchResult, outcome.playResult)
  ) {
    return getRunnerPlayResults(results, before, cleared);
  }
  const absorbOuts =
    edited.runsScored ||
    (!edited.outsRecorded && isOutResult(outcome.playResult));

  return absorbOuts
    ? {
        ...results,
        outsRecorded: boundNumberToRange(
          0,
          cleared - results.runsScored,
          getMaxOuts(before),
        ),
      }
    : {
        ...results,
        runsScored: boundNumberToRange(
          0,
          cleared - results.outsRecorded,
          getMaxRuns(before),
        ),
      };
};
